'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { faqTranslations } from '@/translations/faq'
import { Listbox } from '@headlessui/react'

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const { language } = useLanguage()
  const t = faqTranslations[language]

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  // Filter FAQs based on selected category
  const filteredFAQs = selectedCategory === 'All' 
    ? t.faqs 
    : t.faqs.filter(faq => faq.category === selectedCategory)

  return (
    <section id="faq" className="py-24 bg-gradient-to-b from-gray-50 to-gray-100" aria-labelledby="faq-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 id="faq-heading" className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t.title}
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Category Selector */}
        {/* Desktop: Button group */}
        <div className="hidden lg:flex flex-wrap justify-center gap-4 mt-12">
          {t.categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category)
                setOpenIndex(null) // Close any open FAQ when changing category
              }}
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 border focus:outline-none ${
                selectedCategory === category
                  ? 'bg-gray-800 text-white shadow-lg border-gray-800'
                  : 'bg-white text-gray-600 border-gray-200 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Mobile/Tablet: Dropdown */}
        <div className="lg:hidden mt-8 mb-8 relative">
          <Listbox value={selectedCategory} onChange={(category) => {
            setSelectedCategory(category)
            setOpenIndex(null) // Close any open FAQ when changing category
          }}>
            {({ open }) => (
              <div>
                <Listbox.Button className={`w-full px-6 py-4 text-left bg-white rounded-lg shadow-sm focus:outline-none transition-all duration-200 border ${open ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-300'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-gray-900">{selectedCategory}</span>
                    <ChevronDown className="h-5 w-5 text-gray-600" aria-hidden="true" />
                  </div>
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-sm border border-gray-100 max-h-60 overflow-auto focus:outline-none">
                  {t.categories.map(category => (
                    <Listbox.Option
                      key={category}
                      value={category}
                      className={({ active }) =>
                        `relative cursor-pointer select-none px-6 py-4 ${active ? 'bg-gray-50 text-gray-800' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`
                      }
                    >
                      {({ selected }) => (
                        <span className={`block text-lg font-medium ${selected ? 'text-gray-800' : 'text-gray-600'}`}>{category}</span>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            )}
          </Listbox>
        </div>

        <div className="mt-12 max-w-3xl mx-auto">
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left focus:outline-none"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      {faq.question}
                    </h3>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-600 transition-transform duration-200 ${
                        openIndex === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                <div
                  id={`faq-${index}`}
                  className={`px-6 transition-all duration-200 ease-in-out ${
                    openIndex === index ? 'pb-4' : 'h-0 overflow-hidden'
                  }`}
                >
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600">
            {t.contactText}{' '}
            <a
              href="#contact"
              className="font-medium text-gray-800 hover:text-gray-900"
            >
              {t.contactLink}
            </a>{' '}
            for more information.
          </p>
        </div>
      </div>
    </section>
  )
} 